import {
  AlertTriangle,
  CheckCircle2,
  Lightbulb,
  ShieldCheck,
} from "lucide-react";

import type {
  HouseholdInsight,
  HouseholdIntelligenceResponse,
} from "@/api/householdIntelligence";
import { Card } from "@/components/ui/card";

type Props = {
  intelligence: HouseholdIntelligenceResponse;
};

export function HouseholdInsightsCard({ intelligence }: Props) {
  const insights = intelligence.insights;

  return (
    <Card className="p-6">
      <div className="flex items-center gap-3">
        <div className="rounded-xl bg-violet-500/15 p-3 text-violet-400">
          <Lightbulb size={21} />
        </div>

        <div>
          <h2 className="text-xl font-semibold text-white">
            Household Insights
          </h2>

          <p className="text-sm text-slate-500">
            Patterns influencing household health
          </p>
        </div>
      </div>

      {insights.length === 0 ? (
        <div className="mt-5 rounded-2xl border border-emerald-500/20 bg-emerald-500/5 p-6 text-center">
          <CheckCircle2 className="mx-auto text-emerald-400" size={26} />

          <p className="mt-3 font-semibold text-emerald-300">
            No significant concerns detected
          </p>

          <p className="mt-2 text-sm leading-6 text-slate-400">
            MinistryIQ has not identified any immediate household-level
            attendance, care, or engagement concerns.
          </p>
        </div>
      ) : (
        <div className="mt-5 space-y-3">
          {insights.map((insight, index) => (
            <InsightRow
              key={`${insight.title}-${index}`}
              insight={insight}
            />
          ))}
        </div>
      )}
    </Card>
  );
}

function InsightRow({ insight }: { insight: HouseholdInsight }) {
  const visual = getSeverityVisual(insight.severity);

  return (
    <div
      className={`rounded-2xl border p-5 ${visual.containerClass}`}
    >
      <div className="flex items-start gap-3">
        <div className={`mt-0.5 shrink-0 ${visual.iconClass}`}>
          {visual.icon}
        </div>

        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <p className="font-semibold text-white">{insight.title}</p>

            <span
              className={`rounded-full px-2.5 py-1 text-xs font-semibold ${visual.badgeClass}`}
            >
              {formatSeverity(insight.severity)}
            </span>
          </div>

          <p className="mt-3 text-sm leading-6 text-slate-300">
            {insight.description}
          </p>

          <div className="mt-4 border-t border-slate-800/80 pt-4">
            <p className="text-xs font-semibold uppercase tracking-wider text-slate-500">
              Recommended response
            </p>

            <p className="mt-2 text-sm leading-6 text-slate-400">
              {insight.recommendation}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}

function getSeverityVisual(severity: string) {
  const normalized = severity.toUpperCase();

  if (["LOW", "INFO", "HEALTHY"].includes(normalized)) {
    return {
      icon: <ShieldCheck size={19} />,
      iconClass: "text-emerald-400",
      badgeClass: "bg-emerald-500/15 text-emerald-300",
      containerClass:
        "border-emerald-500/20 bg-emerald-500/5",
    };
  }

  if (["MEDIUM", "WATCH", "WARNING"].includes(normalized)) {
    return {
      icon: <AlertTriangle size={19} />,
      iconClass: "text-amber-400",
      badgeClass: "bg-amber-500/15 text-amber-300",
      containerClass:
        "border-amber-500/20 bg-amber-500/5",
    };
  }

  return {
    icon: <AlertTriangle size={19} />,
    iconClass: "text-rose-400",
    badgeClass: "bg-rose-500/15 text-rose-300",
    containerClass:
      "border-rose-500/20 bg-rose-500/5",
  };
}

function formatSeverity(severity: string) {
  return severity.replaceAll("_", " ");
}